// Formatter functions
const formatters = new Map([
  ['full', (date, locale) => date.toLocaleString(locale, { dateStyle: 'full', timeStyle: 'medium' })],
  ['long', (date, locale) => date.toLocaleString(locale, { dateStyle: 'long', timeStyle: 'medium' })],
  ['medium', (date, locale) => date.toLocaleString(locale, { dateStyle: 'medium', timeStyle: 'medium' })],
  ['mediumWithoutSeconds', (date, locale) => date.toLocaleString(locale, { dateStyle: 'medium', timeStyle: 'short' })],
  ['short', (date, locale) => date.toLocaleString(locale)],
  ['dateOnly', (date, locale) => date.toLocaleDateString(locale, { dateStyle: 'long' })],
  ['timeOnly', (date, locale) => date.toLocaleTimeString(locale, { timeStyle: 'medium' })],
  ['timeOnlyShort', (date, locale) => date.toLocaleTimeString(locale, { timeStyle: 'short' })],
  ['iso', (date) => date.toISOString()],
  ['relative', (date) => {
    const now = new Date();
    const diffInSeconds = Math.floor((date - now) / 1000); // Positive for future, negative for past
    const absDiffInSeconds = Math.abs(diffInSeconds);
    const diffInMinutes = Math.floor(absDiffInSeconds / 60);
    const diffInHours = Math.floor(diffInMinutes / 60);
    const diffInDays = Math.floor(diffInHours / 24);
    const diffInMonths = Math.floor(diffInDays / 30);

    if (absDiffInSeconds < 30) return diffInSeconds > 0 ? 'just now' : 'just now';
    if (absDiffInSeconds < 60) return `${absDiffInSeconds} seconds ${diffInSeconds > 0 ? 'to start' : 'ago'}`;
    if (diffInMinutes < 60) return `${diffInMinutes} ${diffInMinutes === 1 ? 'minute' : 'minutes'} ${diffInSeconds > 0 ? 'to start' : 'ago'}`;
    if (diffInHours < 24) return `${diffInHours} ${diffInHours === 1 ? 'hour' : 'hours'} ${diffInSeconds > 0 ? 'to start' : 'ago'}`;
    if (diffInDays < 30) return `${diffInDays} ${diffInDays === 1 ? 'day' : 'days'} ${diffInSeconds > 0 ? 'to start' : 'ago'}`;
    if (diffInMonths < 12) return `${diffInMonths} ${diffInMonths === 1 ? 'month' : 'months'} ${diffInSeconds > 0 ? 'to start' : 'ago'}`;

    // Fallback to medium date format for dates older than a year
    return formatters.get('medium')(date, navigator.language);
  }],
]);

// DateFormatter function
const DateFormatter = (name, dateInput, locale = navigator?.language || 'en-US') => {
  if (dateInput === undefined || dateInput === null || dateInput === '') {
    return '';
  }
  let date = dateInput;

  if (typeof date === 'string') {
    // Check if the date string already contains a timezone offset (Z or ±hh:mm)
    if (!date.match(/(Z|[+-]\d{2}:\d{2})$/)) {
      date += 'Z'; // Append Z to force UTC interpretation
    }
  }
  if (!(date instanceof Date)) {
    // eslint-disable-next-line no-param-reassign
    date = new Date(date);
  }
  // Check if the 'date' is a valid Date object
  if (isNaN(date.getTime())) {
    throw new Error('Invalid date');
  }

  const formatter = formatters.get(name);

  if (!formatter) {
    throw new Error(`Unknown formatter: ${name}`);
  }
  return formatter(date, locale);
};

// Exported formatters
const fullDateFormatter = (date, locale) => DateFormatter('full', date, locale);
const longDateFormatter = (date, locale) => DateFormatter('long', date, locale);
const mediumDateFormatter = (date, locale) => DateFormatter('medium', date, locale);
const mediumWithoutSecondsFormatter = (date, locale) => DateFormatter('mediumWithoutSeconds', date, locale); // New Export
const shortDateFormatter = (date, locale) => DateFormatter('short', date, locale);
const dateOnlyFormatter = (date, locale) => DateFormatter('dateOnly', date, locale);
const timeOnlyFormatter = (date, locale) => DateFormatter('timeOnly', date, locale);
const timeOnlyShortFormatter = (date, locale) => DateFormatter('timeOnlyShort', date, locale);
const isoDateFormatter = (date) => DateFormatter('iso', date);
const relativeDateFormatter = (date) => DateFormatter('relative', date);

export {
  fullDateFormatter,
  longDateFormatter,
  mediumDateFormatter,
  mediumWithoutSecondsFormatter,
  shortDateFormatter,
  dateOnlyFormatter,
  timeOnlyFormatter,
  timeOnlyShortFormatter,
  isoDateFormatter,
  relativeDateFormatter,
};
